import { TopbarCustomSlot } from '@cw-elements/host-kit';
import { loadRemoteModule } from '@cw-elements/mfe-setup/load';

import { TopbarNavigationMFEProps, TopbarNavigationSkeleton } from '@cw-elements/mfe-setup/util';
import type { AvailableLanguages, AvailableUnits } from '@cw-elements/profile-dropdown/utils/contants';
import { logo_protector_topbar } from 'assets';
import { TOP_BAR_ELEMENT_ID } from 'config/constants';
import { selectSystemFlags } from 'core/core.reducer';
import { getCurrentUnitSystem, getInterfaceLanguage } from 'core/core.selectors';
import { handleRemoteModuleError, retryFunction } from 'core/utils/functions';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as ReportIcon } from 'st-protector-icons/Minor/Report.svg';
import STReportDrawerRender from '../header/components/report/report-drawer.component';
import { handleLogout } from '../header/header.utils';
import { useFarmSettingsMenuProps } from '../header/hooks/use-farm-settings-menu-props.hook';
import { ReportsOverlayDrawer } from './reports-overlay-drawer.component';
import { SeasonPopoverWrapper } from './season/season-popover-wrapper.component';
import './topbar.less';
import { useTopBar } from './use-topbar.hook';

const RETRY_ATTEMPTS = 5;
const RETRY_DELAY_MS = 5000;

const TOP_BAR_REMOTE_MODULE = 'topbar-navigation/index';

const importTopBarNavigation = () =>
  retryFunction(async () => await loadRemoteModule(TOP_BAR_REMOTE_MODULE), RETRY_ATTEMPTS, RETRY_DELAY_MS);

const TopBarNavigation = lazy<React.FC<TopbarNavigationMFEProps>>(() =>
  importTopBarNavigation().catch(handleRemoteModuleError(TOP_BAR_REMOTE_MODULE))
);

const preloadTopBarChunksOnModuleLoad = () => {
  const ignorePreloadFailure = () => undefined;
  void importTopBarNavigation().catch(ignorePreloadFailure);
};

preloadTopBarChunksOnModuleLoad();

const TopBarContainer = () => {
  const [t] = useTranslation();

  const systemFlags = useSelector(selectSystemFlags);
  const currentLanguage = useSelector(getInterfaceLanguage);
  const currentUnitSystem = useSelector(getCurrentUnitSystem);

  const segmentTracking = useSegmentTracking();
  const supportedLanguages: AvailableLanguages[] = [
    'en',
    'en-GB',
    'pt-BR',
    'pt-PT',
    'es',
    'es-AR',
    'pl',
    'de',
    'el',
    'fr',
    'hu',
    'it',
    'ru'
  ];
  const unitSystem: AvailableUnits[] = ['metric', 'imperial'];

  const dispatch = useDispatch();

  const {
    onFarmSelect,
    onGoToHallOfCompanies,
    onGoToHallOfFarms,
    onOrganizationSelect,
    handleCurrentLanguage,
    handleCurrentUnitSystem,
    handleOpenReportDrawer,
    onWorkspaceSelect,
    onGoToWorkspaceHall,
    featureConfig,
    shouldShowReportIcon,
    selectedOrgId,
    selectedPropertyId,
    selectedWorkspaceId,
    entitiesSelectorAdditionalData,
    entitiesSelectorOnNavigate
  } = useTopBar();

  const productSettings = useFarmSettingsMenuProps({
    workspaceId: selectedWorkspaceId,
    orgId: selectedOrgId
  });

  return (
    <Suspense
      fallback={
        <TopbarNavigationSkeleton
          featureConfig={{
            hasSettingsMenu: true,
            hasProfileDropdown: true,
            hasDeckOfApps: true
          }}
        />
      }>
      <div className='container-topbar'>
        <TopBarNavigation
          id={TOP_BAR_ELEMENT_ID}
          userLanguage={currentLanguage}
          onLogout={() => handleLogout({ segmentTracking, systemFlags, dispatch })}
          defaultItems={{
            selectedWorkspace: {
              value: selectedWorkspaceId
            },
            selectedOrganization: { value: selectedOrgId },
            selectedFarm: { value: selectedPropertyId }
          }}
          productImage={{
            redirectUrl: '/hall-of-companies',
            imageSrc: logo_protector_topbar
          }}
          featureConfig={featureConfig}
          entitiesSelectorAdditionalData={entitiesSelectorAdditionalData}
          entitiesSelectorOnNavigate={entitiesSelectorOnNavigate}
          profileDropdownConfig={() => ({
            key: currentUnitSystem,
            availableLanguages: supportedLanguages,
            availableUnitSystems: unitSystem,
            onLanguageChange: handleCurrentLanguage,
            onUnitSystemChange: handleCurrentUnitSystem
          })}
          appData={{
            name: 'Protector',
            id: process.env.VITE_PROTECTOR_APP_ID
          }}
          workspaceDropdown={() => ({
            onSelect: onWorkspaceSelect,
            goToEntity: onGoToWorkspaceHall
          })}
          organizationDropdown={() => ({
            onSelect: onOrganizationSelect,
            goToEntity: onGoToHallOfCompanies
          })}
          farmDropdown={() => ({
            onSelect: onFarmSelect,
            goToEntity: onGoToHallOfFarms
          })}
          settingsMenuConfig={() => ({
            workspaceId: selectedWorkspaceId,
            orgId: selectedOrgId,
            propertyId: selectedPropertyId,
            productSettings
          })}
          additionalItems={() =>
            shouldShowReportIcon
              ? [
                  {
                    tooltipText: t('report.tooltip'),
                    icon: (
                      <div className='report-icon-container' data-testid='topbar-report-open'>
                        <ReportIcon height={20} width={20} />
                      </div>
                    ),
                    onClick: handleOpenReportDrawer
                  }
                ]
              : []
          }>
          <TopbarCustomSlot>{!featureConfig.hideEntitiesDropdown && <SeasonPopoverWrapper />}</TopbarCustomSlot>
        </TopBarNavigation>
      </div>

      <STReportDrawerRender />
      <ReportsOverlayDrawer />
    </Suspense>
  );
};

export default TopBarContainer;
